<template>
  <ActionButton
    class="order-action-button"
    :actions-by-status="actionsByStatus"
    :status="order.status"
  />
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import { computed, PropType, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { ActionCommand, RygenMenuItem } from '@rygen/rygen-ui/model';

import {
  Order,
  OrderAction,
  OrderStatus,
  Shipment,
  ShipmentStatus,
  ShipOrder,
} from '@/model';
import { isStatusEqualOrBefore } from '@/util';

import ActionButton from '@rygen/rygen-ui/components/button/ActionButton.vue';

const props = defineProps({
  order: {
    type: Order,
    required: true,
  },
  shipments: {
    type: Array as PropType<Shipment[]>,
    default: () => [],
  },
  shipOrders: {
    type: Array as PropType<ShipOrder[]>,
    default: () => [],
  },
});
const { order, shipments, shipOrders } = toRefs(props);
const emit = defineEmits<{
  (event: 'command', payload: ActionCommand<OrderAction>): void;
}>();

const isRouteBuilderEnabled = useFlag('route-builder-feat-flag');
const isShipmentBuilderEnabled = useFlag('shipment-builder-feat-flag');

const { t } = useI18n();

const hasTenderedShipment = computed(() => {
  return shipments.value.some((s) => {
    return !isStatusEqualOrBefore(
      s.status ?? ShipmentStatus.PLANNING,
      ShipmentStatus.CARRIER_ASSIGNED,
    );
  });
});

const isAssignedToShipment = computed(() => shipments.value.length > 0);

const unplannedShipOrders = computed(() => {
  return shipOrders.value.filter((so) => !so.shipmentId);
});

const createEmptyAction = (action: OrderAction) => ({
  key: action,
  label: '',
  command: () => emit('command', { action }),
  visible: false,
});

const actions: Ref<{
  [orderAction in OrderAction]: RygenMenuItem;
}> = computed(() => ({
  [OrderAction.ADD_TAGS]: createEmptyAction(OrderAction.ADD_TAGS),
  [OrderAction.CANCEL]: {
    key: OrderAction.CANCEL,
    label: t('common.cancelParam', { param: t('orders.orders') }),
    icon: PrimeIcons.TIMES,
    command: () => emit('command', { action: OrderAction.CANCEL }),
    requiredAuthorization: ['order', 'update'],
    visible: !hasTenderedShipment.value,
  },
  [OrderAction.CLONE]: {
    key: OrderAction.CLONE,
    label: t('common.duplicateParam', { param: t('orders.orders') }),
    icon: PrimeIcons.COPY,
    command: () => emit('command', { action: OrderAction.CLONE }),
    requiredAuthorization: ['order', 'create'],
  },
  [OrderAction.CONSOLIDATE]: {
    key: OrderAction.CONSOLIDATE,
    label: t('consolidation.consolidate'),
    icon: PrimeIcons.TRUCK,
    to: {
      name: 'shipOrderConsolidation',
      query: { orderId: order.value.id },
    },
    requiredAuthorization: ['shipment', 'consolidate'],
    visible: isShipmentBuilderEnabled.value && !isAssignedToShipment.value,
  },
  [OrderAction.REQUEST_SPOT_MARKET_BID]: {
    key: OrderAction.REQUEST_SPOT_MARKET_BID,
    label: t('carrierPortal.spotMarket.requestBid'),
    icon: PrimeIcons.DOLLAR,
    command: () =>
      emit('command', { action: OrderAction.REQUEST_SPOT_MARKET_BID }),
    visible: !isAssignedToShipment.value,
  },
  [OrderAction.SEND_TO_ROUTE_BUILDER]: {
    key: OrderAction.SEND_TO_ROUTE_BUILDER,
    label: t('routeBuilder.sendToRouteBuilder'),
    icon: PrimeIcons.TRUCK,
    to: {
      name: 'routeBuilder',
      query: {
        orderId: order.value.id,
      },
    },
    requiredAuthorization: ['shipment', 'consolidate'],
    visible: isRouteBuilderEnabled.value && !!unplannedShipOrders.value.length,
  },
  [OrderAction.SHARE]: {
    key: OrderAction.SHARE,
    label: t('common.shareParam', {
      param: t('shipments.trackingLink'),
    }),
    icon: PrimeIcons.USER_PLUS,
    command: () => emit('command', { action: OrderAction.SHARE }),
    visible: isAssignedToShipment.value,
  },
  [OrderAction.ASSIGN_USERS]: createEmptyAction(OrderAction.ASSIGN_USERS),
}));

const actionsByStatus: Ref<{
  [status in OrderStatus]: RygenMenuItem[];
}> = computed(() => ({
  [OrderStatus.PENDING]: [
    actions.value[OrderAction.REQUEST_SPOT_MARKET_BID],
    actions.value[OrderAction.SEND_TO_ROUTE_BUILDER],
    actions.value[OrderAction.CONSOLIDATE],
    actions.value[OrderAction.CLONE],
    actions.value[OrderAction.CANCEL],
  ],
  [OrderStatus.IN_PROCESS]: [
    actions.value[OrderAction.CLONE],
    actions.value[OrderAction.SHARE],
    actions.value[OrderAction.CANCEL],
  ],
  [OrderStatus.SHIPPED]: [
    actions.value[OrderAction.CLONE],
    actions.value[OrderAction.SHARE],
  ],
  [OrderStatus.DELIVERED]: [
    actions.value[OrderAction.CLONE],
    actions.value[OrderAction.SHARE],
  ],
  [OrderStatus.CUSTOMER_REVIEW]: [
    actions.value[OrderAction.CLONE],
    actions.value[OrderAction.SHARE],
  ],
  [OrderStatus.BILLED]: [
    actions.value[OrderAction.CLONE],
    actions.value[OrderAction.SHARE],
  ],
  [OrderStatus.COMPLETE]: [actions.value[OrderAction.CLONE]],
  [OrderStatus.CANCELED]: [actions.value[OrderAction.CLONE]],
}));
</script>
